import { useState } from 'react'
import { loadTopicsExpanded, saveTopicsExpanded } from '../../utils/feed-prefs.js'

const TYPE_FILTERS = [
  { id: 'federal',   label: 'Federal',    palette: 'federal' },
  { id: 'state',     label: 'State',      palette: 'state' },
  { id: 'city',      label: 'City',       palette: 'city' },
  { id: 'candidate', label: 'Candidates', palette: 'candidate' },
  { id: 'user',      label: 'User',       palette: 'user' },
]

// UX67: temporal narrowing. Reads item activity (last legislative
// action date, with postedAt as a fallback) and scopes the feed to
// recently-moved bills. "Any time" is the default — mainstream users
// never lose anything until they explicitly narrow.
const TIMEFRAME_FILTERS = [
  { id: 'all',   label: 'Any time' },
  { id: 'today', label: 'Today' },
  { id: 'week',  label: 'This week' },
  { id: 'month', label: 'This month' },
]

export function FeedControls({
  scope,
  onScopeChange,
  query,
  onQueryChange,
  activeTypes,
  onToggleType,
  topic,
  onTopicChange,
  presentTopics = [],
  // UX55: topic subscriptions.
  subscribedTopics = [],
  onToggleSubscribe,
  followedOnly = false,
  onToggleFollowedOnly,
  // UX67: temporal filter.
  timeframe = 'all',
  onTimeframeChange,
}) {
  const [topicsExpanded, setTopicsExpanded] = useState(() => loadTopicsExpanded())
  const toggleTopicsExpanded = () => {
    setTopicsExpanded((prev) => {
      const next = !prev
      saveTopicsExpanded(next)
      return next
    })
  }
  // Topics that aren't visible in collapsed view. If the user has a
  // topic selected, it sits next to "All topics" so they can see the
  // active filter; the count excludes it.
  const hiddenCount = topic
    ? Math.max(0, presentTopics.length - 1)
    : presentTopics.length
  return (
    <section className="feed-controls">
      <div className="scope-toggle" role="tablist" aria-label="Scope">
        <button
          type="button"
          className={scope === 'mine' ? 'active' : ''}
          onClick={() => onScopeChange('mine')}
        >
          My jurisdiction
        </button>
        <button
          type="button"
          className={scope === 'all' ? 'active' : ''}
          onClick={() => onScopeChange('all')}
        >
          All
        </button>
      </div>

      <input
        type="search"
        className="feed-search"
        placeholder="Search by title, bill ID, or jurisdiction…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        aria-label="Search polls"
      />

      {/* UX67: timeframe narrows the feed to recently-moved bills.
          Sits above the type pills so the two filters read as a
          vertical ladder: when → what. */}
      <div className="timeframe-pills" role="radiogroup" aria-label="Timeframe">
        {TIMEFRAME_FILTERS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`timeframe-pill ${timeframe === t.id ? 'active' : ''}`}
            onClick={() => onTimeframeChange?.(t.id)}
            aria-pressed={timeframe === t.id}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="type-pills">
        {TYPE_FILTERS.flatMap((t, i) => {
          const on = activeTypes.has(t.id)
          const btn = (
            <button
              key={t.id}
              type="button"
              className={`type-pill ${on ? `active ${t.palette}` : ''}`}
              onClick={() => onToggleType(t.id)}
              aria-pressed={on}
            >
              {t.label}
            </button>
          )
          // Force a row break before the 4th pill (candidates) on narrow
          // viewports — keeps levels on row 1, kinds on row 2.
          return i === 3
            ? [<span key="break" className="type-pill-break" aria-hidden="true" />, btn]
            : [btn]
        })}
      </div>

      {presentTopics.length > 0 && (
        <div className="topic-pills" role="group" aria-label="Topics">
          <button
            type="button"
            className={`topic-pill ${!topic ? 'active' : ''}`}
            onClick={() => onTopicChange('')}
            aria-pressed={!topic}
          >
            All topics
          </button>
          {/* UX55: "Followed only" filter toggle, shown only when the
              user has at least one subscription. Collapses the feed to
              polls whose topic (case-insensitive) matches one of their
              subs. */}
          {subscribedTopics.length > 0 && (
            <button
              type="button"
              className={`topic-pill topic-pill-followed ${followedOnly ? 'active' : ''}`}
              onClick={() => onToggleFollowedOnly?.()}
              aria-pressed={followedOnly}
              title={`Show polls only in your ${subscribedTopics.length} followed topic${subscribedTopics.length === 1 ? '' : 's'}`}
            >
              ★ Followed
            </button>
          )}
          {topicsExpanded
            ? presentTopics.map((c) => {
                const on = topic === c
                const subbed = subscribedTopics.includes(c.toLowerCase())
                return (
                  <span key={c} className="topic-pill-wrap">
                    <button
                      type="button"
                      className={`topic-pill ${on ? 'active' : ''} ${subbed ? 'subbed' : ''}`}
                      onClick={() => onTopicChange(on ? '' : c)}
                      aria-pressed={on}
                    >
                      {c}
                    </button>
                    {onToggleSubscribe && (
                      <button
                        type="button"
                        className={`topic-sub-star ${subbed ? 'on' : ''}`}
                        onClick={() => onToggleSubscribe(c)}
                        title={subbed ? `Unfollow ${c}` : `Follow ${c}`}
                        aria-label={subbed ? `Unfollow ${c}` : `Follow ${c}`}
                        aria-pressed={subbed}
                      >
                        {subbed ? '★' : '☆'}
                      </button>
                    )}
                  </span>
                )
              })
            : topic && (
                <button
                  type="button"
                  className="topic-pill active"
                  onClick={() => onTopicChange('')}
                  aria-pressed
                >
                  {topic}
                </button>
              )}
          {topicsExpanded ? (
            <button
              type="button"
              className="feed-topic-toggle"
              onClick={toggleTopicsExpanded}
              aria-expanded
            >
              Show less ↑
            </button>
          ) : hiddenCount > 0 && (
            <button
              type="button"
              className="feed-topic-toggle"
              onClick={toggleTopicsExpanded}
              aria-expanded={false}
            >
              + {hiddenCount} more {hiddenCount === 1 ? 'topic' : 'topics'} ↓
            </button>
          )}
        </div>
      )}

      <div className="feed-legend" aria-hidden="true">
        <span className="swatch-row">
          <span className="swatch" style={{ background: 'var(--yes-bar)' }} />
          Resident vote
        </span>
        <span className="swatch-row">
          <span className="swatch" style={{ background: 'var(--yes-nr-bar)' }} />
          Non-resident vote
        </span>
      </div>
    </section>
  )
}
